import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  Mail,
  ShieldCheck,
  UserRound,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';

const roleLabel = (role: string): string => {
  const labels: Record<string, string> = {
    owner: 'Propriétaire',
    admin: 'Administrateur',
    coop_admin: 'Administrateur',
    org_admin: 'Administrateur',
    farm_manager: 'Responsable d’exploitation',
    farmer: 'Exploitant agricole',
    viewer: 'Consultation',
  };
  return labels[role] || role;
};

const passwordFailureMessage = (error: any): string => {
  const code = error?.name || error?.code || error?.response?.data?.code;
  if (code === 'NotAuthorizedException') {
    return 'Le mot de passe actuel est incorrect.';
  }
  if (code === 'InvalidPasswordException') {
    return 'Le nouveau mot de passe ne respecte pas la politique de sécurité du compte.';
  }
  if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
    return 'Trop de tentatives ont été effectuées. Réessayez dans quelques minutes.';
  }
  return 'Le mot de passe n’a pas pu être modifié. Réessayez ou contactez Agri Safe si le problème persiste.';
};

const SoverdiProfileSecurityR7: React.FC = () => {
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setSuccess(false);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setFormError('Renseignez les trois champs avant de continuer.');
      return;
    }
    if (newPassword.length < 8) {
      setFormError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    if (newPassword === currentPassword) {
      setFormError('Le nouveau mot de passe doit être différent du mot de passe actuel.');
      return;
    }

    try {
      setSubmitting(true);
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(true);
    } catch (error: any) {
      setFormError(passwordFailureMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const newPasswordLongEnough = newPassword.length >= 8;
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="soverdi-profile-security-r7">
      <section>
        <button
          type="button"
          onClick={() => navigate('/r6-soverdi-settings')}
          className="mb-3 inline-flex min-h-[40px] items-center gap-2 rounded-lg px-2 text-sm font-medium text-[var(--color-primary)] transition-colors hover:bg-[var(--gray-100)]"
          data-testid="profile-security-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Paramètres
        </button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[var(--color-primary)]">
              <ShieldCheck className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">Compte client</span>
            </div>
            <h2 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">Profil & sécurité</h2>
            <p className="mt-1 max-w-2xl text-sm text-[var(--text-secondary)]">
              Consultez votre identité de connexion et gérez votre mot de passe Agri Safe.
            </p>
          </div>
          <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
            Accès personnel
          </span>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.85fr_1.15fr]">
        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
              <UserRound className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Identité du compte</p>
              <h3 className="mt-1 break-words text-lg font-semibold text-[var(--text-primary)]" data-testid="profile-security-name">
                {user?.name || '—'}
              </h3>
            </div>
          </div>

          <div className="mt-5 space-y-3 rounded-xl bg-[var(--gray-50)] p-4 text-sm">
            <div className="flex items-start gap-2">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
              <div className="min-w-0">
                <p className="text-xs text-[var(--text-tertiary)]">Adresse e-mail</p>
                <p className="break-all font-medium text-[var(--text-primary)]" data-testid="profile-security-email">{user?.email || '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
              <div>
                <p className="text-xs text-[var(--text-tertiary)]">Rôle</p>
                <p className="font-medium text-[var(--text-primary)]" data-testid="profile-security-role">
                  {user?.role ? roleLabel(user.role) : '—'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-start gap-3 rounded-xl border border-[var(--border-light)] p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-primary)]" />
            <div>
              <p className="font-semibold text-[var(--text-primary)]">Compte authentifié</p>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Votre session utilise l’authentification Agri Safe. Les informations techniques de connexion ne sont jamais affichées dans l’espace client.
              </p>
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-[var(--text-tertiary)]">
            Le nom, l’adresse e-mail et le rôle sont affichés en lecture seule. Leur modification nécessite une gestion de compte dédiée et n’est pas simulée dans cette interface.
          </p>
        </section>

        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
              <KeyRound className="h-5 w-5 text-[var(--color-primary)]" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Sécurité</p>
              <h3 className="mt-1 text-lg font-semibold text-[var(--text-primary)]">Changer mon mot de passe</h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                La modification est appliquée à votre compte d’authentification réel.
              </p>
            </div>
          </div>

          <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="profile-current-password" className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
                Mot de passe actuel
              </label>
              <div className="relative">
                <input
                  id="profile-current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  disabled={submitting}
                  className="min-h-[44px] w-full rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 py-2.5 pr-11 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  aria-label={showCurrentPassword ? 'Masquer le mot de passe actuel' : 'Afficher le mot de passe actuel'}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="profile-new-password" className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  id="profile-new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={submitting}
                  className="min-h-[44px] w-full rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 py-2.5 pr-11 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  aria-label={showNewPassword ? 'Masquer le nouveau mot de passe' : 'Afficher le nouveau mot de passe'}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="profile-confirm-password" className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
                Confirmer le nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  id="profile-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={submitting}
                  className="min-h-[44px] w-full rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 py-2.5 pr-11 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/10 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  aria-label={showConfirmPassword ? 'Masquer la confirmation' : 'Afficher la confirmation'}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid gap-2 text-xs text-[var(--text-tertiary)] sm:grid-cols-2">
              <div className={`flex items-center gap-2 ${newPasswordLongEnough ? 'text-emerald-700' : ''}`}>
                <CheckCircle2 className="h-4 w-4" />
                Au moins 8 caractères
              </div>
              <div className={`flex items-center gap-2 ${passwordsMatch ? 'text-emerald-700' : ''}`}>
                <CheckCircle2 className="h-4 w-4" />
                Confirmation identique
              </div>
            </div>

            {formError && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="profile-password-error">
                {formError}
              </div>
            )}

            {success && (
              <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" role="status" data-testid="profile-password-success">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Votre mot de passe a été mis à jour.</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="min-h-[44px] w-full rounded-xl bg-[var(--color-primary)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="profile-password-submit"
            >
              {submitting ? 'Modification en cours…' : 'Mettre à jour le mot de passe'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default SoverdiProfileSecurityR7;
